import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, BookOpen, ChevronDown, CircleHelp, Search, ShieldCheck, X } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/ui";
import { canViewAdmin } from "../lib/domain";
import { adminHelp, memberHelp, type HelpItem, type HelpSection } from "../lib/helpContent";
import type { LexData } from "../lib/types";

type HelpTab = "using" | "admin";

const sectionId = (title: string) => `help-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;

const TAG_RULES: { test: RegExp; tag: string }[] = [
  { test: /observer|sign[- ]?in|signing in|browse|contact details/, tag: "Access" },
  { test: /becoming a member|admission|membership|propose your firm|co-sign|endorse/, tag: "Membership" },
  { test: /vote|voting|quorum|amendment|ballot|procuration|delegat/, tag: "Voting" },
  { test: /mandate|outreach|legislat|joint position|drafting/, tag: "Legislative" },
  { test: /cooperation|expertise|referral|local counsel|exchange/, tag: "Expertise" },
  { test: /profile|photo|surname|notification/, tag: "Profile" },
  { test: /dashboard|kpi|participation rate|recognition|balance|transparency/, tag: "Governance" },
  { test: /public site|publish/, tag: "Publishing" },
  { test: /report|audit|export|system health/, tag: "Records" },
  { test: /firm|user|role|account|registry|invitation|mfa|suspend|moderation/, tag: "Admin" },
  { test: /document|attach|knowledge|resource|note|memorand/, tag: "Documents" },
];

function tagForItem(item: HelpItem): string {
  if (item.tag) return item.tag;
  const haystack = `${item.title} ${item.body}`.toLowerCase();
  return TAG_RULES.find((rule) => rule.test.test(haystack))?.tag ?? "Guide";
}

export function HelpPage({ data }: { data: LexData; mode?: "convex" | "demo" }) {
  const isAdmin = canViewAdmin(data.viewer);
  const [tab, setTab] = useState<HelpTab>("using");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const [activeSection, setActiveSection] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const effectiveTab: HelpTab = tab === "admin" && !isAdmin ? "using" : tab;
  const base = effectiveTab === "admin" ? adminHelp : memberHelp;
  const q = query.trim().toLowerCase();

  const sections = useMemo<{ section: HelpSection; items: HelpItem[] }[]>(() => {
    if (!q) return base.map((section) => ({ section, items: section.items }));
    return base
      .map((section) => ({ section, items: section.items.filter((item) => `${item.title} ${item.body}`.toLowerCase().includes(q) || section.title.toLowerCase().includes(q)) }))
      .filter((entry) => entry.items.length > 0);
  }, [base, q]);

  // Focus search with "/" (unless already typing in a field).
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey) return;
      const target = event.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA"].includes(target.tagName)) return;
      event.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Scroll-spy for the "On this page" list.
  useEffect(() => {
    if (sections.length === 0) return;
    setActiveSection((current) => current || sections[0].section.title);
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveSection(visible.target.getAttribute("data-section") ?? "");
      },
      { rootMargin: "-12% 0px -72% 0px", threshold: 0 },
    );
    for (const { section } of sections) {
      const element = document.getElementById(sectionId(section.title));
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [sections, effectiveTab]);

  const toggle = (key: string) => setOpen((current) => { const next = new Set(current); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  const scrollTo = (title: string) => { setActiveSection(title); document.getElementById(sectionId(title))?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <>
      <PageHeader title="Help" subtitle="How LexMosaic works, feature by feature. Search a topic or browse by section." />

      <div className="mb-6 flex items-center gap-2.5 rounded-xl border border-line bg-surface px-4 py-3 focus-within:border-primary">
        <Search className="h-5 w-5 shrink-0 text-muted" />
        <input ref={searchRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={'Search help — e.g. "votes", "observer", "publishing"…'} className="min-w-0 flex-1 bg-transparent text-[15px] text-ink outline-none placeholder:text-muted" />
        {query ? (
          <button type="button" aria-label="Clear search" onClick={() => setQuery("")} className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted hover:bg-hover hover:text-ink"><X className="h-4 w-4" /></button>
        ) : (
          <kbd className="hidden shrink-0 rounded-md border border-line bg-canvas px-2 py-0.5 font-mono text-[12px] text-muted sm:block">/</kbd>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[256px_minmax(0,1fr)] lg:items-start">
        <aside className="grid gap-4 lg:sticky lg:top-4">
          {isAdmin ? (
            <div className="inline-flex gap-1 rounded-xl border border-line bg-divider p-1">
              <button type="button" onClick={() => setTab("using")} className={`inline-flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-[13.5px] font-semibold transition ${effectiveTab === "using" ? "bg-surface text-primary shadow-sm" : "text-secondary hover:text-ink"}`}><BookOpen className="h-4 w-4" /> Using</button>
              <button type="button" onClick={() => setTab("admin")} className={`inline-flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-[13.5px] font-semibold transition ${effectiveTab === "admin" ? "bg-surface text-primary shadow-sm" : "text-secondary hover:text-ink"}`}><ShieldCheck className="h-4 w-4" /> Admin</button>
            </div>
          ) : null}

          <div>
            <div className="mb-2 px-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted">On this page</div>
            <nav className="grid gap-0.5">
              {sections.map(({ section, items }) => {
                const active = activeSection === section.title;
                return (
                  <button key={section.title} type="button" onClick={() => scrollTo(section.title)} className={`flex items-start justify-between gap-2 rounded-lg px-3 py-2 text-left text-[14px] transition ${active ? "bg-tint font-semibold text-primary" : "text-secondary hover:bg-hover hover:text-ink"}`}>
                    <span className="flex items-start gap-2"><span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${active ? "bg-primary" : "bg-line"}`} />{section.title}</span>
                    <span className="mt-0.5 shrink-0 text-[12px] text-muted">{items.length}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="rounded-xl border border-line bg-surface p-4">
            <div className="text-[15px] font-semibold text-ink">Still stuck?</div>
            <p className="mt-1.5 text-[13px] leading-5 text-secondary">Your firm representative or a network administrator can help with access and membership.</p>
            <Link to="/members-directory" className="mt-3 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-primary hover:underline">Contact an administrator <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </aside>

        <div className="grid gap-8">
          {sections.length === 0 ? (
            <div className="rounded-xl border border-dashed border-line bg-surface px-6 py-12 text-center text-[14px] text-muted">No help topics match “{query}”.</div>
          ) : sections.map(({ section, items }) => (
            <section key={section.title} id={sectionId(section.title)} data-section={section.title} className="scroll-mt-4">
              <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-line pb-3">
                <h2 className="text-[22px] font-semibold tracking-[-0.01em] text-ink">{section.title}</h2>
                {section.summary ? <span className="text-[13.5px] text-secondary">{section.summary}</span> : null}
              </div>
              <div className="grid gap-2.5">
                {items.map((item) => {
                  const key = `${section.title}::${item.title}`;
                  const isOpen = open.has(key) || Boolean(q);
                  return <AccordionItem key={key} item={item} isOpen={isOpen} onToggle={() => toggle(key)} />;
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </>
  );
}

function AccordionItem({ item, isOpen, onToggle }: { item: HelpItem; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className={`overflow-hidden rounded-xl border bg-surface transition ${isOpen ? "border-ink/15 shadow-soft" : "border-line"}`}>
      <button type="button" onClick={onToggle} aria-expanded={isOpen} className="flex w-full items-start gap-3 px-4 py-3.5 text-left">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-tint text-primary"><CircleHelp className="h-4 w-4" /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-[16px] font-semibold text-ink">{item.title}</span>
          {!isOpen ? <span className="mt-0.5 block truncate text-[13.5px] text-secondary">{item.body}</span> : null}
        </span>
        <ChevronDown className={`mt-1 h-5 w-5 shrink-0 text-muted transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen ? (
        <div className="px-4 pb-4 pl-[68px]">
          <p className="whitespace-pre-line text-[14px] leading-6 text-secondary">{item.body}</p>
          <span className="mt-3 inline-block rounded border border-infoBorder bg-infoBg px-2 py-0.5 font-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-navy">{tagForItem(item)}</span>
        </div>
      ) : null}
    </div>
  );
}
